import type { Branch } from "@domain/CRM/branch";
import type { Deal } from "@domain/CRM/deal";
import type { AgrianTypes, AgrianEventSelections } from "@domain/Integrations/Agrian";
import { pickDefaultEventsForFields } from "@domain/Integrations/Agrian";
import { Job } from "@domain/CRM/job";
import { normalizePointsFC } from "@domain/Geo/sample";
import type { User } from "@domain/Auth";
import type { AgrianType, IAgrianApi } from "@ports/integrations/IAgrianApi";
import type { IAgrianGeoFetcher } from "@ports/integrations/IAgrianGeoFetcher";

export interface AgrianUseCasesDeps {
	agrianApi: IAgrianApi;
	geoFetcher: IAgrianGeoFetcher;
}

interface LoadOrganizationsInput {
	user: User | undefined;
	agrianType: AgrianType;
	/** Org IDs derived from the active company + selected branches. Used as
	 *  the by-ids fallback when the user is not an admin (i.e. they're
	 *  scoped to a specific subset of orgs). */
	currentOrgIds: string[];
	forceRefresh?: boolean;
}

interface FarmsAndFieldsResult {
	farms: AgrianTypes.Farm[];
	fields: AgrianTypes.Field[];
	/** Pre-computed (n−1) deselection subsets for the caller's grower-set
	 *  cache: keyed by sorted comma-joined grower-ids. The vm seeds these
	 *  into its own cache so deselecting a grower is a hit, not a refetch. */
	subsets: Map<string, { farms: AgrianTypes.Farm[]; fields: AgrianTypes.Field[] }>;
}

interface PrefetchEventsInput {
	fieldIds: string[];
	fields: AgrianTypes.Field[];
	agrianType: AgrianType;
	isZoneDeal: boolean;
	/** Company setting: source only from pre-existing sample events. Suppresses the target-event
	 *  and zone-map fetches as well as their candidacy. */
	preExistingEventsOnly?: boolean;
	/** Optional progress reporter; the GUI threads this into a wizard
	 *  progress bar. CLI/tests can omit it. */
	onProgress?: (progress: { current: number; total: number; label?: string }) => void;
}

interface PrefetchEventsResult {
	targetEvents: AgrianTypes.HydratedTargetEvent[];
	sampleEvents: AgrianTypes.HydratedSampleEvent[];
	zones: AgrianTypes.HydratedZone[];
	selections: AgrianEventSelections;
	hasAmbiguity: boolean;
	/** Ids of fields that ended with no usable event. Under `preExistingEventsOnly` these are
	 *  exactly the fields `buildJobsFromSelections` will skip. */
	fieldsWithoutEvents: string[];
}

interface BuildJobsInput {
	selectedFields: AgrianTypes.Field[];
	farms: AgrianTypes.Farm[];
	selectedGrowers: AgrianTypes.Grower[];
	prefetched: {
		targetEvents: AgrianTypes.HydratedTargetEvent[];
		sampleEvents: AgrianTypes.HydratedSampleEvent[];
		zones: AgrianTypes.HydratedZone[];
	};
	selections: AgrianEventSelections;
	deal: Deal | null;
	branch: Branch | null;
	user: User | undefined;
	isZoneDeal: boolean;
	/** Company setting: build jobs only for fields whose selection is a sample event, and stamp
	 *  that event's id onto `job.eventId`. */
	preExistingEventsOnly?: boolean;
	/** Per-field cache lookup; when supplied AND it returns a Job, that
	 *  field is reused verbatim (no geo-fetcher calls). */
	getFromCache?: (fieldId: string) => Job | undefined;
	/** Per-field cache write; called whenever a fresh Job is built. */
	setInCache?: (fieldId: string, job: Job) => void;
}

interface BuildJobsResult {
	jobs: Job[];
	featureStats: { zones: number; centroids: number; cores: number };
}

/**
 * Drives the Agrian-import workflow IO. The vm keeps its UI state, the
 * three Map-keyed caches (organizations / growers / farms-fields by
 * grower-set / per-field jobs), the React refs that buildJobs reads
 * synchronously after prefetch, and the auto-load cascade — but every
 * fetch+dedup+derivation step lives here.
 */
export interface AgrianUseCases {
	loadOrganizations(input: LoadOrganizationsInput): Promise<AgrianTypes.Organization[]>;
	loadGrowers(orgIds: string[], agrianType: AgrianType, forceRefresh?: boolean): Promise<AgrianTypes.Grower[]>;
	loadFarmsAndFields(
		growerIds: string[],
		agrianType: AgrianType,
		forceRefresh?: boolean,
	): Promise<FarmsAndFieldsResult>;
	prefetchEventsForFields(input: PrefetchEventsInput): Promise<PrefetchEventsResult>;
	buildJobsFromSelections(input: BuildJobsInput): Promise<BuildJobsResult>;
}

async function loadOrganizations(
	deps: AgrianUseCasesDeps,
	input: LoadOrganizationsInput,
): Promise<AgrianTypes.Organization[]> {
	let loaded: AgrianTypes.Organization[] = [];
	if (input.user?.isAdmin) {
		loaded = await deps.agrianApi.getOrganizations(input.agrianType, input.forceRefresh);
	} else if (input.currentOrgIds.length > 1) {
		loaded = await deps.agrianApi.getOrganizationsByIds(input.currentOrgIds, input.agrianType, input.forceRefresh);
	}
	return Array.from(new Map(loaded.map((o) => [o.id, o])).values());
}

async function loadGrowers(
	deps: AgrianUseCasesDeps,
	orgIds: string[],
	agrianType: AgrianType,
	forceRefresh?: boolean,
): Promise<AgrianTypes.Grower[]> {
	const loaded = await deps.agrianApi.getGrowers(orgIds, agrianType, forceRefresh);
	return Array.from(new Map(loaded.map((g) => [g.id, g])).values());
}

async function loadFarmsAndFields(
	deps: AgrianUseCasesDeps,
	growerIds: string[],
	agrianType: AgrianType,
	forceRefresh?: boolean,
): Promise<FarmsAndFieldsResult> {
	const loadedFarms = await deps.agrianApi.getFarms(growerIds, agrianType, forceRefresh);
	const farms = Array.from(new Map(loadedFarms.map((f) => [f.id, f])).values());
	const farmIds = farms.map((f) => f.id);
	const loadedFields = await deps.agrianApi.getFields(farmIds, agrianType, forceRefresh);
	const fields = Array.from(new Map(loadedFields.map((f) => [f.id, f])).values());

	const subsets = new Map<string, { farms: AgrianTypes.Farm[]; fields: AgrianTypes.Field[] }>();
	for (let i = 0; i < growerIds.length; i++) {
		const subsetIds = growerIds.filter((_, j) => j !== i);
		if (subsetIds.length > 0) {
			const subKey = [...subsetIds].sort().join(",");
			if (!subsets.has(subKey)) {
				const subFarms = farms.filter((f) => subsetIds.includes(f.grower_id));
				const subFarmIds = new Set(subFarms.map((f) => f.id));
				const subFields = fields.filter((f) => subFarmIds.has(f.farm_id));
				subsets.set(subKey, { farms: subFarms, fields: subFields });
			}
		}
	}

	return { farms, fields, subsets };
}

async function prefetchEventsForFields(
	deps: AgrianUseCasesDeps,
	input: PrefetchEventsInput,
): Promise<PrefetchEventsResult> {
	const empty: PrefetchEventsResult = {
		targetEvents: [],
		sampleEvents: [],
		zones: [],
		selections: {},
		hasAmbiguity: false,
		fieldsWithoutEvents: [],
	};
	if (input.fieldIds.length === 0) return empty;

	// Pre-existing companies can never select a target event or a zone map, so skip fetching
	// them entirely — fewer round-trips, and `totalSteps` drops to match so the progress bar
	// stays honest.
	const fetchNonSampleSources = !input.preExistingEventsOnly;

	const totalSteps = input.fieldIds.length + (fetchNonSampleSources ? 1 + (input.isZoneDeal ? 1 : 0) : 0);
	let loaded = 0;
	input.onProgress?.({ current: 0, total: totalSteps, label: "Loading sampling events…" });

	let targetEvents: AgrianTypes.HydratedTargetEvent[] = [];
	if (fetchNonSampleSources) {
		input.onProgress?.({ current: loaded, total: totalSteps, label: "Loading target events…" });
		targetEvents = await deps.agrianApi.getTargetEvents(input.fieldIds, input.agrianType);
		loaded++;
	}

	let zones: AgrianTypes.HydratedZone[] = [];
	if (fetchNonSampleSources && input.isZoneDeal) {
		input.onProgress?.({ current: loaded, total: totalSteps, label: "Loading zones…" });
		zones = await deps.agrianApi.getZones(input.fieldIds, input.agrianType);
		loaded++;
	}

	const sampleEvents: AgrianTypes.HydratedSampleEvent[] = [];
	for (const field of input.fields) {
		input.onProgress?.({ current: loaded, total: totalSteps, label: `Sampling events — ${field.name}` });
		const fieldSamples = await deps.agrianApi.getSampleEvents([field.id], input.agrianType);
		sampleEvents.push(...fieldSamples);
		loaded++;
	}

	const { selections, hasAmbiguity, fieldsWithoutEvents } = pickDefaultEventsForFields(
		input.fields,
		sampleEvents,
		targetEvents,
		zones,
		{ preExistingEventsOnly: input.preExistingEventsOnly },
	);

	return { targetEvents, sampleEvents, zones, selections, hasAmbiguity, fieldsWithoutEvents };
}

async function buildJobsFromSelections(deps: AgrianUseCasesDeps, input: BuildJobsInput): Promise<BuildJobsResult> {
	const { targetEvents, sampleEvents, zones } = input.prefetched;
	const newJobs: Job[] = [];

	for (const field of input.selectedFields) {
		const choice = input.selections[field.id];

		// Resolve the chosen sample event ONCE, up front — under `preExistingEventsOnly` it is
		// both the eligibility test and the source of `event_code` and geometry.
		//
		// Resolving (not just checking `choice.source`) is what closes the gap: a choice naming
		// a sample event that is absent from `prefetched.sampleEvents` passes a source-only
		// check, then finds nothing, and the job is built with no points, no zones and an empty
		// eventId — precisely the boundary-only job this setting exists to eliminate. Callers
		// today pass selections and prefetch from the same call, but this function is exported
		// and independently callable.
		const chosenSampleEvent =
			choice?.source === "sample" ? sampleEvents.find((e) => e.id === choice.eventId) : undefined;

		// Checked BEFORE the cache lookup: `getFromCache` returns a previously-built Job
		// verbatim, so a check placed after it would let an ineligible field through on any
		// second pass.
		if (input.preExistingEventsOnly && !chosenSampleEvent) continue;

		const cached = input.getFromCache?.(field.id);
		if (cached) {
			newJobs.push(cached);
			continue;
		}
		const farm = input.farms.find((f) => f.id === field.farm_id);
		if (!farm) continue;
		const grower = input.selectedGrowers.find((g) => g.id === farm.grower_id);
		if (!grower) continue;

		const boundaryGeoJSON = deps.geoFetcher.getBoundaryGeoJSON([field.boundary_map]);

		let pointsGeoJSON: import("geojson").FeatureCollection | undefined;
		let zonesGeoJSON: import("geojson").FeatureCollection | undefined;
		// Agrian's `event_code` — the short sync code (e.g. "8PW4-95AQ") that Rogo's "Event ID"
		// column holds, per that field's own definition ("Unique code for the sample event …
		// Purpose: sync data back to software"). Deliberately NOT `choice.eventId`, which is the
		// record UUID and only ever a lookup key.
		let sampleEventCode: string | undefined;

		if (choice) {
			if (choice.source === "sample") {
				const event = chosenSampleEvent;
				if (event) {
					sampleEventCode = event.event_code;
					const centroids = deps.geoFetcher.getSampleEventCentroidsGeoJSON(event);
					const cores = deps.geoFetcher.getSampleEventCoresGeoJSON(event);
					const zns = deps.geoFetcher.getSampleEventZonesGeoJSON(event);
					pointsGeoJSON = input.isZoneDeal ? (cores ?? undefined) : (centroids ?? undefined);
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			} else if (choice.source === "target") {
				const event = targetEvents.find((e) => e.id === choice.eventId);
				if (event) {
					const centroids = deps.geoFetcher.getTargetEventCentroidsGeoJSON(event);
					const zns = deps.geoFetcher.getTargetEventZonesGeoJSON(event);
					if (!input.isZoneDeal) pointsGeoJSON = centroids ?? undefined;
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			} else if (choice.source === "zone-map") {
				const event = zones.find((e) => e.id === choice.eventId);
				if (event) {
					const zns = deps.geoFetcher.getZoneMapGeoJSON(event);
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			}
		}

		const newJob: Job = Job.defaultForImport({
			fieldName: field.name,
			depth: input.deal?.depth,
			density: input.deal?.density,
			growerName: grower.name,
			frequencyOfMicros: input.deal?.frequencyOfMicros,
			testPackage: input.deal?.testPackage,
			farmName: farm.name,
		});

		newJob.files = {
			pointsGeoJSON: pointsGeoJSON ? normalizePointsFC(pointsGeoJSON) : undefined,
			boundaryGeoJSON,
			zonesGeoJSON,
		};
		newJob.dealIds = input.deal?.id ? [input.deal.id] : [];
		newJob.externalFarmId = farm.id;
		newJob.fieldId = field.id;
		newJob.fieldPriority = "Regular Turn (default)";
		newJob.submitterEmail = input.user?.email;
		newJob.submitterName = input.user?.name;
		newJob.branchIds = input.branch ? [input.branch.id] : [];
		newJob.externalGrower = { id: grower.id, name: grower.name };
		// Pre-existing companies carry the sourced sample event's code through, so the downstream
		// sendback targets that event instead of creating a new one. Otherwise it stays empty and
		// `upsert_agrian_sample_event` fills it in later.
		//
		// A sample event with no `event_code` leaves this empty rather than skipping the job:
		// the field is required for these companies, so it surfaces as a fixable error the user
		// can fill in, which beats silently dropping the field.
		newJob.eventId = input.preExistingEventsOnly ? (sampleEventCode ?? "") : "";

		input.setInCache?.(field.id, newJob);
		newJobs.push(newJob);
	}

	const featureStats = {
		zones: newJobs.reduce((sum, j) => sum + (j.files.zonesGeoJSON?.features?.length || 0), 0),
		centroids: newJobs.reduce(
			(sum, j) => sum + (input.isZoneDeal ? 0 : j.files.pointsGeoJSON?.features?.length || 0),
			0,
		),
		cores: newJobs.reduce((sum, j) => sum + (input.isZoneDeal ? j.files.pointsGeoJSON?.features?.length || 0 : 0), 0),
	};

	return { jobs: newJobs, featureStats };
}

export function makeAgrianUseCases(deps: AgrianUseCasesDeps): AgrianUseCases {
	return {
		loadOrganizations: (input) => loadOrganizations(deps, input),
		loadGrowers: (orgIds, agrianType, forceRefresh) => loadGrowers(deps, orgIds, agrianType, forceRefresh),
		loadFarmsAndFields: (growerIds, agrianType, forceRefresh) =>
			loadFarmsAndFields(deps, growerIds, agrianType, forceRefresh),
		prefetchEventsForFields: (input) => prefetchEventsForFields(deps, input),
		buildJobsFromSelections: (input) => buildJobsFromSelections(deps, input),
	};
}
